import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import {
  Box,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

const HIDE_CONTACT_INFORMATION = true;  // Hide the contact information on OrganizationDetailsPage. Remove when fully implemented.

const ui = {
  pageBg: "#f7f8fa",
  panel: "#ffffff",
  railBg: "#ffffff",
  border: "#e6e9ee",
  borderStrong: "#d9dee7",
  text: "#182230",
  muted: "#667085",
  subtle: "#98a2b3",
  soft: "#f8fafc",
  successBg: "#eefaf2",
  successBorder: "#b7e4c4",
  successText: "#17733b",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: { xs: 40, sm: 36 },
    borderRadius: 0.75,
    bgcolor: "#fff",
    fontSize: 12.8,
    "& fieldset": { borderColor: ui.borderStrong },
    "&:hover fieldset": { borderColor: "#c5ccd6" },
    "&.Mui-focused fieldset": { borderWidth: 1, borderColor: "primary.main" },
    "&.Mui-focused": {
      boxShadow: theme => `0 0 0 3px ${theme.palette.primary.main}12`,
    },
  },
  "& .MuiOutlinedInput-input": {
    height: { xs: 40, sm: 36 },
    boxSizing: "border-box",
    px: 1.15,
    py: 0,
  },
  "& .MuiSelect-select": {
    minHeight: { xs: "40px !important", sm: "36px !important" },
    display: "flex",
    alignItems: "center",
    py: "0 !important",
  },
};

const labelSx = {
  display: "block",
  mb: 0.45,
  color: ui.muted,
  fontSize: 11.2,
  lineHeight: 1.25,
  fontWeight: 650,
};

const formGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
    lg: "repeat(12, minmax(0, 1fr))",
  },
  columnGap: { xs: 1.25, sm: 1.5, lg: 2 },
  rowGap: { xs: 1.25, sm: 1.45 },
  alignItems: "start",
};

const gridSpan = (desktopColumns, tabletColumns = 1) => ({
  gridColumn: {
    xs: "1 / -1",
    sm: `span ${tabletColumns}`,
    lg: `span ${desktopColumns}`,
  },
});

const formatCustomerId = (id) =>
  (Number.isInteger(id) &&
    id >= 0 &&
    String(id).padStart(9, '0').replace(/\B(?=(\d{3})+(?!\d))/g, '-')) || null;

const OrganizationInformation = ({
  disabled,
  draft,
  onChange,
}) => {
  const intl = useIntl();

  const updateField = field => event => {
    onChange?.(field, event.target.value);
  };

  const displayedCompanyId = formatCustomerId(draft.publicCompanyId);

  const handleCopyCompanyId = async () => {
    if (!displayedCompanyId || typeof navigator === "undefined" || !navigator.clipboard) return;
    await navigator.clipboard.writeText(String(displayedCompanyId));
  };

  const renderField = ({ label, value, onChange, required = false, type, placeholder }) => (
    <Box>
      <Typography component="label" sx={labelSx}>
        {label}
        {required && <Box component="span" sx={{ color: "error.main" }}> *</Box>}
      </Typography>
      <TextField
        fullWidth
        size="small"
        type={type}
        disabled={disabled}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        sx={inputSx}
      />
    </Box>
  );

  return (
    <>
      <Box sx={{ px: { xs: 1.35, sm: 1.75, md: 2.5 }, py: { xs: 1.55, sm: 1.9, md: 2.2 } }}>
        <Typography sx={{ fontSize: 15.5, fontWeight: 600, color: ui.text, lineHeight: 1.25 }}>
          <FormattedMessage id="admin.organizationDetails.basicSettings" />
        </Typography>
        <Typography sx={{ mt: 0.25, mb: 1.7, color: ui.muted, fontSize: 11.7, lineHeight: 1.45 }}>
          <FormattedMessage id="admin.organizationDetails.basicSettings.subTitle" />
        </Typography>

        <Box sx={formGridSx}>
          <Box sx={gridSpan(8)}>
            {renderField({
              label: intl.formatMessage({ id: "admin.organizationDetails.name" }),
              value: draft.name,
              onChange: updateField("name"),
              required: true,
            })}
          </Box>

          <Box sx={gridSpan(4)}>
            {renderField({
              label: intl.formatMessage({ id: "admin.organizationDetails.website" }),
              value: draft.website,
              onChange: updateField("website"),
              placeholder: "https://",
            })}
          </Box>

          <Box sx={gridSpan(4)}>
            <Typography sx={labelSx}>
              <FormattedMessage id="admin.organizationDetails.companyId" />
            </Typography>
            <Box
              sx={{
                height: { xs: 40, sm: 36 },
                px: 1.1,
                border: `1px solid ${ui.borderStrong}`,
                borderRadius: 0.75,
                bgcolor: ui.soft,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12.9,
                  fontWeight: 700,
                  color: ui.subtle,
                  letterSpacing: "0.01em",
                }}
              >
                {displayedCompanyId}
              </Typography>
              {displayedCompanyId && (
                <Tooltip title={intl.formatMessage({ id: "admin.organizationDetails.copyCompanyId" })}>
                  <IconButton
                    size="small"
                    onClick={handleCopyCompanyId}
                    sx={{ width: 24, height: 24, color: ui.muted }}
                  >
                    <ContentCopyOutlinedIcon sx={{ fontSize: 13.5 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography
              sx={{
                mt: 0.38,
                minHeight: 28,
                color: ui.muted,
                fontSize: 10.45,
                lineHeight: 1.35,
              }}
            >
              <FormattedMessage id="admin.organizationDetails.companyId.help" />
            </Typography>
          </Box>

          <Box sx={gridSpan(4)}>
            {renderField({
              label: intl.formatMessage({ id: "admin.organizationDetails.organizationNumber" }),
              value: draft.organizationNumber,
              onChange: updateField("organizationNumber"),
            })}
          </Box>

          <Box sx={gridSpan(4, 2)}>
            {renderField({
              label: intl.formatMessage({ id: "admin.organizationDetails.vatNumber" }),
              value: draft.vatNumber,
              onChange: updateField("vatNumber"),
            })}
          </Box>
        </Box>
      </Box>

      {!HIDE_CONTACT_INFORMATION && (<>

        <Divider sx={{ borderColor: ui.border }} />

        <Box sx={{ px: { xs: 1.35, sm: 1.75, md: 2.5 }, py: { xs: 1.55, sm: 1.9, md: 2.15 } }}>
          <Typography sx={{ fontSize: 15.5, fontWeight: 600, color: ui.text, lineHeight: 1.25 }}>
            <FormattedMessage id="admin.organizationDetails.contactInformation" />
          </Typography>
          <Typography sx={{ mt: 0.25, mb: 1.7, color: ui.muted, fontSize: 11.7, lineHeight: 1.45 }}>
            <FormattedMessage id="admin.organizationDetails.contactInformation.subTitle" />
          </Typography>

          <Box sx={formGridSx}>
            <Box sx={gridSpan(4)}>
              {renderField({
                label: intl.formatMessage({ id: "admin.organizationDetails.contactPerson" }),
                value: draft.contactPerson,
                onChange: updateField("contactPerson"),
              })}
            </Box>

            <Box sx={gridSpan(8)}>
              {renderField({
                label: intl.formatMessage({ id: "admin.organizationDetails.email" }),
                value: draft.email,
                onChange: updateField("email"),
                type: "email",
              })}
            </Box>

            <Box sx={gridSpan(4)}>
              {renderField({
                label: intl.formatMessage({ id: "admin.organizationDetails.telephone" }),
                value: draft.telephone,
                onChange: updateField("telephone"),
              })}
            </Box>

            <Box sx={gridSpan(8)}>
              {renderField({
                label: intl.formatMessage({ id: "admin.organizationDetails.reference" }),
                value: draft.reference,
                onChange: updateField("reference"),
              })}
            </Box>
          </Box>
        </Box>

      </>)}
    </>
  );
};

OrganizationInformation.propTypes = {
  disabled: PropTypes.bool.isRequired,
  draft: PropTypes.shape({
    contactPerson: PropTypes.string,
    email: PropTypes.string,
    name: PropTypes.string,
    organizationNumber: PropTypes.string,
    publicCompanyId: PropTypes.number,
    reference: PropTypes.string,
    telephone: PropTypes.string,
    vatNumber: PropTypes.string,
    website: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

OrganizationInformation.defaultProps = {
  disabled: false,
  draft: undefined,
  onChange: undefined,
};

OrganizationInformation.displayName = "OrganizationInformation";

export default OrganizationInformation;
